'use client';

import { Panel, PanelContent, PanelHeader, PanelBody, PanelClose } from '@pickleballinc/ui/components/ui/panel';
import { DialogTitle, DialogHeader, DialogDescription } from '@pickleballinc/ui/components/ui/dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Avatar, Checkbox } from '@pickleballinc/react-ui';

import { useState } from 'react';
import { Radio } from '@pickleballinc/react-ui';
import { useSearchParams } from 'next/navigation';
import { faClose, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@/components/atoms';
import { UserCard } from '@/components/organisms/ScoreCards/subs/UserCard';
import Input from '@/components/organisms/Input';
import { Matches, PhotoUrls, SubMatch, TeamLeaguePlayerInfo } from '@/entities';
import { subStepContent } from '@/components/constants/constants';
import { User } from '@/entities/SystemUsers';
import { useDebounce } from '@pickleballinc/ui/components/team-leagues-movements/utils/use-debounce';
import { RotationPlayerInfo, RotationsInfo } from '@/entities/rotations/RotationsInfo';
import { Path } from '@pickleballinc/ui/components/team-leagues-movements/_components/path';
import { Step } from '@pickleballinc/ui/components/team-leagues-movements/_components/step';
import { toast } from 'react-toastify';
import { useSystemUsersSearchQuery } from '@/hooks/hooks';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useQueryClient } from '@tanstack/react-query';
import { useTieBreakerMutation } from '@/hooks/tie-breaker/useRotationsMutation';
import { useTieBreakerMatch } from '@/hooks/tie-breaker/useTieBreakerMatch';
import { ContactCardSkeleton } from '@/components/organisms/skeletons/ContactCardSkeleton';
import { Country } from '@/entities/Country';
import { State } from '@/entities/State';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { TieBreakerFormatType } from '@/entities/Matchup';
import { useMatchupSettingsQuery } from '@/hooks/matchup/useMatchupSettingsQuery';

export interface Rotation {
	uuid: string;
	matchUuid: string;
	playerOneUuid: string;
	playerTwoUuid?: string;
	rotationOrder: number;
	team: number;
}

interface SubPlayerModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	type: 'team' | 'event' | 'users';
	rotationsInfo?: RotationsInfo;
	teamOnePlayers: TeamLeaguePlayerInfo[];
	teamTwoPlayers: TeamLeaguePlayerInfo[];
	teamOneTitle: string;
	teamTwoTitle: string;
	countries: Country[];
	states: State[];
	substitutePlayers: TeamLeaguePlayerInfo[];
	matches: SubMatch[];
	matchupUuid: string;
	matchupData?: Matches;
}

export const determinePlayerPhotoUrl = (photo?: PhotoUrls) => {
	if (!photo) return undefined;
	const keys: (keyof PhotoUrls)[] = [
		'headshot',
		'fullImage',
		'featuredImage',
		'overviewImage',
		'profileImage',
		'headerImage',
		'featuredUserImage',
		'actionUserImage'
	];

	for (const key of keys) {
		if (photo[key]?.url) {
			return photo[key]!.url;
		}
	}

	return undefined;
};

export const SubPlayerModal = ({
	open,
	onOpenChange,
	type,
	teamOnePlayers,
	teamTwoPlayers,
	teamOneTitle,
	teamTwoTitle,
	countries,
	states,
	matches,
	substitutePlayers,
	rotationsInfo,
	matchupUuid,
	matchupData
}: SubPlayerModalProps) => {
	const queryClient = useQueryClient();

	const [isLoadingSubmit, setIsLoading] = useState(false);
	const [step, setStep] = useState(1);
	const [subbedOut, setSubbedOut] = useState<TeamLeaguePlayerInfo>();
	const [team, setTeam] = useState<1 | 2>();
	const [subbedIn, setSubbedIn] = useState<TeamLeaguePlayerInfo | User>();
	const searchParams = useSearchParams();
	const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
	const debouncedSearch = useDebounce(searchTerm, 700);
	const [selectedMatches, setSelectedMatches] = useState<string[]>([]);

	const { data: usersResponse, isLoading: isLoadingUsers, error } = useSystemUsersSearchQuery(debouncedSearch);

	const { mutate: changePlayerMutation } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success('Successfully updated');
		}
	});

	const { id: tieBreakerMatchId } = useTieBreakerMatch(matchupData);
	const { createRotation } = useTieBreakerMutation(tieBreakerMatchId);

	const { data: matchupSettings } = useMatchupSettingsQuery(matchupData?.uuid!);

	const handleCheckboxChange = (matchId: string, isChecked: boolean) => {
		setSelectedMatches((prev) => (isChecked ? [...prev, matchId] : prev.filter((id) => id !== matchId)));
	};

	const getAge = (player: TeamLeaguePlayerInfo | User) => {
		if (player?.dob || (player as TeamLeaguePlayerInfo)?.age) {
			const value = 'Age: ' + (player?.dob ? player.dob : (player as TeamLeaguePlayerInfo).age) + ' | ';
			return value;
		}

		return null;
	};

	const teamOneIDs: string[] = [];
	const teamTwoIDs: string[] = [];

	matches?.forEach((match) => {
		if (match.teamOnePlayerOneUuid) {
			teamOneIDs.push(match.teamOnePlayerOneUuid);
		}
		if (match.teamOnePlayerTwoUuid) {
			teamOneIDs.push(match.teamOnePlayerTwoUuid);
		}

		if (match.teamTwoPlayerOneUuid) {
			teamTwoIDs.push(match.teamTwoPlayerOneUuid);
		}
		if (match.teamTwoPlayerTwoUuid) {
			teamTwoIDs.push(match.teamTwoPlayerTwoUuid);
		}
	});

	rotationsInfo?.teamOne.forEach((rotation) => {
		if (rotation.playerOneUuid) {
			teamOneIDs.push(rotation.playerOneUuid);
		}
		if (rotation.playerTwoUuid) {
			teamOneIDs.push(rotation.playerOneUuid);
		}
	});

	rotationsInfo?.teamTwo.forEach((rotation) => {
		if (rotation.playerOneUuid) {
			teamTwoIDs.push(rotation.playerOneUuid);
		}
		if (rotation.playerTwoUuid) {
			teamTwoIDs.push(rotation.playerOneUuid);
		}
	});

	const filteredTeamOnePlayers: TeamLeaguePlayerInfo[] = teamOnePlayers.filter((player) => teamOneIDs.includes(player.userUuid));
	const filteredTeamTwoPlayers: TeamLeaguePlayerInfo[] = teamTwoPlayers.filter((player) => teamTwoIDs.includes(player.userUuid));

	const closeModal = () => {
		setStep(1);
		setSubbedIn(undefined);
		setSubbedOut(undefined);
		setSelectedMatches([]);
		onOpenChange(false);
	};

	const handleNext = () => {
		if (step === 1) setStep(2);
		else if (step === 2) setStep(3);
		else if (step === 3) setStep(4);
		else if (step === 4) submitSubs();
	};

	const handleBack = () => {
		if (step === 4) setStep(3);
		else if (step === 3) setStep(2);
		else if (step === 2) setStep(1);
		else if (step === 1) closeModal();
	};

	const getEligibleMatches = (allMatches: SubMatch[], subbedOutUuid: string, team: number) => {
		return allMatches.filter((match) => {
			const playsInLineup =
				match.teamOnePlayerOneUuid === subbedOutUuid ||
				match.teamOnePlayerTwoUuid === subbedOutUuid ||
				match.teamTwoPlayerOneUuid === subbedOutUuid ||
				match.teamTwoPlayerTwoUuid === subbedOutUuid;

			if (playsInLineup) return true;

			if (match.isTieBreaker) {
				const teamKey = team === 1 ? 'teamOne' : 'teamTwo';
				const teamRotations = rotationsInfo?.[teamKey] || [];

				const playsInRotation = teamRotations.some(
					(rotation: RotationPlayerInfo) => rotation.playerOneUuid === subbedOutUuid || rotation.playerTwoUuid === subbedOutUuid
				);

				return playsInRotation;
			}

			return false;
		});
	};

	const submitSubs = async () => {
		setIsLoading(true);

		matches
			.filter((match) => selectedMatches.includes(match.matchUuid))
			.map((match) => {
				const subOut = subbedOut?.userUuid;
				const subIn = subbedIn && 'userUuid' in subbedIn ? subbedIn.userUuid : subbedIn?.uuid;
				const teamKey = team === 1 ? 'teamOne' : 'teamTwo';

				const requestPayload: { matchId: string; updateData: any } = {
					matchId: match.matchUuid,
					updateData: {
						lineupChangeIsSubstitution: true
					}
				};

				let shouldCallLineupEP = true;

				if (match.teamOnePlayerOneUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam1UserId1 = subIn;
				} else if (match.teamOnePlayerTwoUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam1UserId2 = subIn;
				} else if (match.teamTwoPlayerOneUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam2UserId1 = subIn;
				} else if (match.teamTwoPlayerTwoUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam2UserId2 = subIn;
				} else {
					shouldCallLineupEP = false;
				}

				if (shouldCallLineupEP) {
					changePlayerMutation(requestPayload, {
						onSuccess: () => {
							queryClient.invalidateQueries({
								queryKey: matchKeys.matches(matchupUuid)
							});
						}
					});
				}

				if (match.isTieBreaker) {
					const teamRotations = rotationsInfo?.[teamKey];
					const rotationsToUpdate: Rotation[] = [];

					let hasChanges = false;

					teamRotations?.forEach((rotation: RotationPlayerInfo) => {
						const newPlayerOneUuid = rotation.playerOneUuid === subOut ? subIn : rotation.playerOneUuid;
						const newPlayerTwoUuid = rotation.playerTwoUuid === subOut ? subIn : rotation.playerTwoUuid;

						if (newPlayerOneUuid !== rotation.playerOneUuid || newPlayerTwoUuid !== rotation.playerTwoUuid) {
							hasChanges = true;
						}

						if (matchupSettings?.data?.tieBreakerFormat.title === TieBreakerFormatType.DOUBLES) {
							rotationsToUpdate.push({
								uuid: rotation.rotationUuid,
								matchUuid: match.matchUuid,
								playerOneUuid: newPlayerOneUuid ?? '',
								playerTwoUuid: newPlayerTwoUuid ?? '',
								rotationOrder: rotation.rotationOrder,
								team: team!
							});
						} else {
							rotationsToUpdate.push({
								uuid: rotation.rotationUuid,
								matchUuid: match.matchUuid,
								playerOneUuid: newPlayerOneUuid ?? '',
								rotationOrder: rotation.rotationOrder,
								team: team!
							});
						}
					});

					if (hasChanges) {
						createRotation.mutate({ rotations: rotationsToUpdate });
					}
				}
			});

		closeModal();
		setIsLoading(false);
	};

	const isNextStepDisabled = () => {
		return (step === 1 && !subbedOut) || (step === 2 && !subbedIn) || (step === 3 && selectedMatches.length === 0);
	};

	const renderPlayer = (player: TeamLeaguePlayerInfo) => {
		return (
			<div key={player.uuid} className="rounded-lg border border-violet-200 bg-violet-25 text-start text-sm shadow-md hover:border-gray-300">
				<div className="flex w-full flex-row items-center justify-between p-4">
					<div className="flex gap-6">
						<div className="flex flex-col items-center text-xs justify-center w-[70px] sm:w-[100px]">
							<Avatar size="xl" imageUrl={determinePlayerPhotoUrl(player.photo)} />
							<p className="max-w-[100px] text-center text-gray-900">
								{states?.find((state) => parseInt(state.id.toString()) === player.stateId)?.title}
							</p>
							<p className="text-gray-500">
								{countries?.find((country) => parseInt(country.id.toString()) === player.countryId)?.abbreviation}
							</p>
						</div>
						<div className="flex flex-col gap-1 text-xs">
							<p className="text-base font-semibold text-gray-900">
								{player.firstName} {player.lastName}
							</p>
							<p>
								{getAge(player)} {player.gender === 'F' ? 'Female' : 'Male'}
							</p>
							<p className="text-wrap break-all">{player.email}</p>
							<p className="text-wrap break-all">
								{player?.country?.internationalCountryCallingCode || ''} {player.phone}
							</p>
							<div className="flex gap-1">
								<p>(D) {player.ratingDoubles ? player.ratingDoubles.toFixed(2) : 'NR'}</p>
								<p>(S) {player.ratingSingles ? player.ratingSingles.toFixed(2) : 'NR'}</p>
							</div>
						</div>
					</div>
					{subbedIn?.uuid !== player.uuid ? (
						<Button className="self-end bg-orange-500 !px-2.5 !py-2 !h-fit !rounded-md" onClick={() => setSubbedIn(player)}>
							<p className="text-xs font-semibold text-white">Select</p>
						</Button>
					) : (
						<Button className="self-end bg-gray-600 !px-2.5 !py-2 !h-fit !rounded-md cursor-not-allowed">
							<p className="text-xs font-semibold text-white">Selected</p>
						</Button>
					)}
				</div>
			</div>
		);
	};

	const renderSub = (player: TeamLeaguePlayerInfo | User, onEdit: () => void) => {
		return (
			<div className="flex w-full items-center justify-between gap-4 rounded-xl border border-gray-200 px-3 py-2">
				<div className="flex gap-2">
					{'photo' in player ? (
						<Avatar size="lg" imageUrl={determinePlayerPhotoUrl(player.photo)} />
					) : (
						'profilePictureUrl' in player && <Avatar size="lg" imageUrl={player.profilePictureUrl} />
					)}
					<div className="flex flex-col gap-0.5 text-xs text-gray-900">
						<p className="text-base font-semibold">
							{player.firstName} {player.lastName}
						</p>
						<p>
							{getAge(player)} {player.gender === 'F' ? 'Female' : 'Male'}
						</p>
						{'ratingDoubles' in player && 'ratingSingles' in player && (
							<div className="flex gap-1">
								<p>(D) {player.ratingDoubles ? player.ratingDoubles.toFixed(2) : 'NR'}</p>
								<p>(S) {player.ratingSingles ? player.ratingSingles.toFixed(2) : 'NR'}</p>
							</div>
						)}
						{'doublesRating' in player && 'singlesRating' in player && (
							<div className="flex gap-1">
								<p>(D) {player.doublesRating ? player.doublesRating.toFixed(2) : 'NR'}</p>
								<p>(S) {player.singlesRating ? player.singlesRating.toFixed(2) : 'NR'}</p>
							</div>
						)}
					</div>
				</div>
				<Button
					className="text-xs font-semibold leading-[18px] text-gray-700 !border !border-gray-300 !px-2 !py-1.5 !h-fit !rounded-md"
					onClick={() => onEdit()}
				>
					Edit
				</Button>
			</div>
		);
	};

	return (
		<Panel open={open} onOpenChange={onOpenChange}>
			<PanelContent className="w-530 flex w-full flex-col" aria-describedby="court-assignment-description">
				<PanelHeader className="flex flex-col border-b border-gray-200 px-6 pb-2 pt-4">
					<div className="flex items-center justify-between">
						<DialogHeader>
							<DialogTitle asChild>
								<h2 className="text-2xl font-semibold text-gray-900">{subStepContent[step]?.title}</h2>
							</DialogTitle>
						</DialogHeader>
						<PanelClose asChild>
							<button
								className="flex size-8 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200"
								aria-label="Close dialog"
							>
								<FontAwesomeIcon icon={faXmark} className="size-5 text-gray-700" />
							</button>
						</PanelClose>
					</div>
					<DialogDescription id="court-assignment-description" className="text-start text-base font-normal text-gray-600">
						{subStepContent[step]?.description}
					</DialogDescription>
				</PanelHeader>

				<PanelBody className="flex-1 px-6 pb-8 pt-4">
					<div className="flex h-full flex-col gap-4">
						<div className="mb-12 flex w-full items-center justify-between px-6 transition-all">
							<Step text="Subbed out" active={step === 1} finished={step > 1} onClick={() => setStep(1)} isOrange={true} />
							<Path active={step > 1} isOrange={true} />
							<Step
								text="Subbed in"
								active={step === 2}
								finished={step > 2}
								onClick={() => !isNextStepDisabled() && setStep(2)}
								isOrange={true}
							/>
							<Path active={step > 2} isOrange={true} />
							<Step
								text="Match & Games"
								active={step === 3}
								finished={step > 3}
								onClick={() => !isNextStepDisabled() && setStep(3)}
								isOrange={true}
							/>
							<Path active={step > 3} isOrange={true} />
							<Step text="Summary" active={step === 4} finished={step > 4} onClick={() => {}} isOrange={true} />
						</div>
						{step === 1 && (
							<div className="flex flex-col gap-4">
								<p className="text-lg font-semibold text-gray-900">{teamTwoTitle}</p>
								<div className="rounded-lg border border-gray-200">
									{filteredTeamTwoPlayers.map((player, index) => (
										<label
											key={player.uuid}
											className={`flex cursor-pointer items-center gap-4 px-4 py-3 ${index % 2 === 1 ? 'bg-gray-100' : ''} ${index === teamTwoPlayers.length - 1 ? 'rounded-lg' : ''}`}
										>
											<Radio
												checked={subbedOut?.userUuid === player.userUuid}
												onChange={() => {
													setSubbedOut(player);
													setTeam(2);
												}}
												size="sm"
												radioTheme="orange"
											/>
											<Avatar size="md" imageUrl={determinePlayerPhotoUrl(player.photo)} />
											<p className="text-base text-gray-900">
												{player.firstName} {player.lastName}
											</p>
										</label>
									))}
								</div>
								<p className="text-lg font-semibold text-gray-900">{teamOneTitle}</p>
								<div className="rounded-lg border border-gray-200">
									{filteredTeamOnePlayers.map((player, index) => (
										<label
											key={player.uuid}
											className={`flex cursor-pointer items-center gap-4 px-4 py-3 ${index % 2 === 1 ? 'bg-gray-100' : ''} ${index === teamOnePlayers.length - 1 ? 'rounded-lg' : ''}`}
										>
											<Radio
												checked={subbedOut?.userUuid === player.userUuid}
												onChange={() => {
													setSubbedOut(player);
													setTeam(1);
												}}
												size="sm"
												radioTheme="orange"
											/>
											<Avatar size="md" imageUrl={determinePlayerPhotoUrl(player.photo)} />

											<p className="text-base text-gray-900">
												{player.firstName} {player.lastName}
											</p>
										</label>
									))}
								</div>
							</div>
						)}
						{step === 2 && (
							<div className="mb-16 flex flex-col gap-2">
								{subbedOut && (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing out</p>
										{renderSub(subbedOut, () => setStep(1))}
									</div>
								)}
								{type === 'team' &&
									(team === 1 ? teamOnePlayers : teamTwoPlayers)
										.filter((player) => player.userUuid !== subbedOut?.userUuid && player.gender === subbedOut?.gender)
										.map((player) => renderPlayer(player))}
								{type === 'event' &&
									substitutePlayers
										.filter((player) => player.userUuid !== subbedOut?.userUuid && player.gender === subbedOut?.gender)
										.map((player) => renderPlayer(player))}
								{type === 'users' && (
									<div className="flex flex-col gap-4 py-4">
										<Input
											PrefixIcon={() => <FontAwesomeIcon icon={faSearch} size="sm" className="text-gray-500" />}
											SuffixIcon={() =>
												searchTerm && (
													<FontAwesomeIcon
														icon={faClose}
														size="sm"
														className="cursor-pointer text-gray-500 hover:text-gray-700"
														onClick={(e) => {
															e.stopPropagation();
															setSearchTerm('');
														}}
													/>
												)
											}
											placeholder="Search by name"
											size={50}
											className="min-w-[200px]"
											inputProps={{ labelClassName: 'text-left text-sm' }}
											onChange={(e) => setSearchTerm(e.target.value)}
											value={searchTerm}
										/>
										{isLoadingUsers && (
											<>
												<ContactCardSkeleton />
												<ContactCardSkeleton />
												<ContactCardSkeleton />
												<ContactCardSkeleton />
											</>
										)}
										{usersResponse?.data
											?.filter((user) => user.uuid !== subbedOut?.userUuid && user.gender === subbedOut?.gender)
											.map((user: User) => (
												<UserCard
													key={user.uuid}
													user={user}
													add={(user: User) => setSubbedIn(user)}
													showRating
													hideButton={subbedIn?.uuid === user.uuid}
												/>
											))}
										{error && (
											<div className="flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-white text-sm">
												<h5 className="font-bold">Error</h5>
												<p className="text-gray-500">{error.message}</p>
											</div>
										)}
									</div>
								)}
							</div>
						)}
						{step === 3 && (
							<div className="flex flex-col gap-6">
								{subbedOut && (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing out</p>
										{renderSub(subbedOut, () => setStep(1))}
									</div>
								)}
								{subbedIn && (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing in</p>
										{renderSub(subbedIn, () => setStep(2))}
									</div>
								)}

								{subbedOut?.userUuid && team && (
									<div className="flex flex-col gap-4">
										<p className="text-lg font-semibold text-gray-900">Select matches</p>

										{getEligibleMatches(matches, subbedOut?.userUuid, team).map((match) => (
											<Checkbox
												key={match.matchUuid}
												size="md"
												theme="orange"
												Text={match.roundText || ''}
												checked={selectedMatches.includes(match.matchUuid)}
												onChange={(e) => handleCheckboxChange(match.matchUuid, e.target.checked)}
												className="cursor-pointer"
											/>
										))}
									</div>
								)}
							</div>
						)}
						{step === 4 && (
							<div className="flex flex-col gap-6">
								{subbedOut && (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing out</p>
										{renderSub(subbedOut, () => setStep(1))}
									</div>
								)}
								{subbedIn && (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing in</p>
										{renderSub(subbedIn, () => setStep(2))}
									</div>
								)}
								<div className="flex flex-col gap-4">
									<div className="flex justify-between pr-3">
										<p className="text-lg font-semibold text-gray-900">Selected matches</p>
										<Button
											className="self-center text-xs font-semibold leading-[18px] text-gray-700 !border !border-gray-300 !px-2 !py-1.5 !h-fit !rounded-md"
											onClick={() => setStep(3)}
										>
											Edit
										</Button>
									</div>
									{matches
										.filter((match) => selectedMatches.includes(match.matchUuid))
										.map((match) => (
											<p key={match.matchUuid} className="text-gray-900">
												{match.roundText}
											</p>
										))}
								</div>
							</div>
						)}

						<div className="pt-20 md:pt-20">
							<div className="fixed inset-x-0 md:inset-x-auto bottom-0 z-50 flex justify-center border-t border-gray-200 bg-white w-full py-4 md:justify-start sm:pr-4 p-4 md:px-0 md:border-none">
								<div className="mr-2 flex w-full flex-col gap-2 sm:w-[525px]">
									<div className="flex flex-row gap-4 pb-4">
										<Button
											onClick={handleBack}
											className="!w-full justify-center border border-gray-300 !bg-white text-base font-semibold !text-gray-700 shadow-xs transition-all duration-200 hover:shadow-sm"
										>
											{step === 1 ? 'Cancel' : 'Back'}
										</Button>

										<Button
											onClick={handleNext}
											className={`flex w-full items-center justify-center border !text-center text-base font-semibold shadow-xs transition-all duration-200 !text-white !bg-orange-500 ${isNextStepDisabled() && 'opacity-50'}`}
											disabled={isLoadingSubmit || isNextStepDisabled()}
										>
											{step === 4 ? 'Save' : 'Next'}
										</Button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</PanelBody>
			</PanelContent>
		</Panel>
	);
};
